// Thread panel — threaded replies on any message. Self-contained: fetches the
// thread, renders root + replies, posts replies, and listens on the channel stream
// for live replies. Fixed right-side glass drawer; full-screen sheet on mobile.
import { useEffect, useRef, useState } from 'react';
import { Agent, api, Message, PublicUser } from './api';
import { Composer } from './Composer';
import { activeTz } from './prefs';
import { sharedSub } from './realtime';

function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: activeTz() });
}
// Minimal, safe autolink for bare URLs (thread replies are plain text in v1).
function renderBody(body: string) {
  return body.split(/(https?:\/\/[^\s]+)/g).map((p, i) =>
    /^https?:\/\//.test(p)
      ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="fx-thread-link">{p}</a>
      : <span key={i} className="fx-thread-span">{p}</span>);
}

function Row({ m, name, avatarUrl, root }: { m: Message; name: string; avatarUrl?: string | null; root?: boolean }) {
  return (
    <div className={`fx-thread-row${root ? ' is-root' : ''}`}>
      <span className="fx-thread-av"
        style={avatarUrl ? { backgroundImage: `url(${avatarUrl})`, color: 'transparent' } : undefined}>
        {avatarUrl ? '' : (name[0] || '?').toUpperCase()}
      </span>
      <div className="fx-thread-main">
        <div className="fx-thread-meta">
          <span className="fx-thread-name">{name}</span>
          <span className="fx-thread-time">{fmtTime(m.created_at)}</span>
        </div>
        <div className="fx-thread-body">
          {renderBody(m.body || '')}
        </div>
      </div>
    </div>
  );
}

export function ThreadPanel({
  rootId, channelId, meId, members, agents = [], onClose,
}: {
  rootId: string;
  channelId: string;
  meId?: string;
  members: PublicUser[];
  agents?: Agent[];
  onClose: () => void;
}) {
  const [root, setRoot] = useState<Message | null>(null);
  const [replies, setReplies] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // @mention autocomplete: the token being typed after "@", or null when closed.
  const [mentionQ, setMentionQ] = useState<string | null>(null);
  const mentionables = [
    ...agents.map((a) => ({ name: a.name, hint: 'agent' })),
    ...members.filter((u) => u.id !== meId).map((u) => ({ name: u.name || u.email || '', hint: '' })),
  ].filter((m) => m.name);
  const mentionMatches = mentionQ === null ? [] : mentionables
    .filter((m) => m.name.toLowerCase().includes(mentionQ.toLowerCase()))
    .slice(0, 6);
  const syncMention = (value: string, caret: number | null) => {
    const upto = value.slice(0, caret ?? value.length);
    const m = upto.match(/(?:^|\s)@([\w .-]{0,30})$/);
    setMentionQ(m ? m[1] : null);
  };
  const pickMention = (name: string) => {
    const el = inputRef.current;
    const caret = el?.selectionStart ?? draft.length;
    const upto = draft.slice(0, caret).replace(/@([\w .-]{0,30})$/, `@${name} `);
    const next = upto + draft.slice(caret);
    setDraft(next);
    setMentionQ(null);
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(upto.length, upto.length); });
  };

  useEffect(() => {
    let alive = true;
    setRoot(null); setReplies([]);
    api.getThread(rootId).then((t) => { if (alive) { setRoot(t.root); setReplies(t.replies); } }).catch(() => {});
    return () => { alive = false; };
  }, [rootId]);

  useEffect(() => {
    const off = sharedSub(`channel:${channelId}`, (ctx: any) => {
      const d = ctx?.data;
      if (d?.type === 'thread.reply' && d.root_id === rootId && d.message) {
        setReplies((cur) => (cur.some((x) => x.id === d.message.id) ? cur : [...cur, d.message]));
      }
    });
    return off;
  }, [channelId, rootId]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [replies.length, root]);

  // Escape closes the drawer, matching the app's other overlays.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const nameOf = (m: Message | null): string => {
    if (!m) return 'Someone';
    return m.sender_name || members.find((u) => u.id === m.sender_id)?.name
      || members.find((u) => u.id === m.sender_id)?.email || 'Someone';
  };
  const avatarOf = (m: Message | null): string | null | undefined =>
    m?.sender_avatar || members.find((u) => u.id === m?.sender_id)?.avatar_url;

  const send = async () => {
    const t = draft.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const r = await api.replyInThread(rootId, t);
      setReplies((cur) => (cur.some((x) => x.id === r.id) ? cur : [...cur, r]));
      setDraft('');
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  return (
    <aside className="fx-thread" role="complementary" aria-label="Thread">
      <header className="fx-thread-head">
        <span className="fx-thread-title">Thread</span>
        <span className="fx-thread-count">
          {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
        </span>
        <span className="fx-thread-spacer" />
        <button type="button" onClick={onClose} title="Close thread" aria-label="Close thread"
          className="fx-thread-x">×</button>
      </header>

      <div className="fx-thread-scroll">
        {root && <Row m={root} name={nameOf(root)} avatarUrl={avatarOf(root)} root />}
        <div className="fx-thread-sep">
          <div className="fx-thread-sep-line" />
          <span className="fx-thread-sep-label">
            {replies.length || 'no'} {replies.length === 1 ? 'reply' : 'replies'}
          </span>
          <div className="fx-thread-sep-line" />
        </div>
        {replies.map((m) => <Row key={m.id} m={m} name={nameOf(m)} avatarUrl={avatarOf(m)} />)}
        <div ref={endRef} />
      </div>

      <div className="fx-thread-foot fx-thread-foot--composer">
        {/* The REAL composer — formatting, @mentions (members + agents), emoji,
            autosize — not a bare input. Media/call affordances are no-ops in
            threads for now (thread attachments are a follow-up). */}
        <Composer
          value={draft}
          placeholder="Reply…"
          spellcheck
          enterSends
          codeFenceEnter
          uploading={false}
          recording={false}
          clipRecording={false}
          hasAttachments={false}
          members={members}
          agents={agents}
          callsEnabled={false}
          editing={false}
          upArrowMode="none"
          onChange={setDraft}
          onSend={send}
          onPickFiles={() => {}}
          onVideo={() => {}}
          onToggleRecord={() => {}}
          onToggleClip={() => {}}
          onCommand={() => {}}
          onSchedule={() => {}}
          onViewScheduled={() => {}}
          onEditLast={() => {}}
          onFocusList={() => {}}
          onCancelEdit={() => {}}
        />
      </div>
    </aside>
  );
}
